import {objectFromEntries} from '@benchling/js-utils';
import React from 'react';

import {TYPES_TO_DISPLAY_NAMES} from 'analysis-tool/AnalysisToolConstants';
import {COLOR_PALETTES} from 'analysis-tool/ui/charting/ChartingConstants';
import {DiscreteColorPalette} from 'analysis-tool/ui/charting/ChartingTypes';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FilterStepFilterConfig from 'data-frames/filters/components/FilterStepFilterConfig';
import findAndAssertColumn from 'data-frames/util/findAndAssertColumn';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import AnalysisFormulaEditor from 'pipelines/formula/components/AnalysisFormulaEditor';
import {AnalysisFormulaParsingError} from 'pipelines/formula/Constants';
import formulaTextToFormulaAstJson from 'pipelines/formula/formulaTextToFormulaAstJson';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import IsInputXToggleGroup from 'pipelines/interpolation/IsInputXToggleGroup';
import {
  GetPipelineStepShape,
  GetTaggedUnionTagValues,
  PipelineJsonSpecToTsType,
} from 'pipelines/PipelineTypes';
import {
  PipelineConfigJsonSpec,
  GetPipelineConfigSpecType,
  StepConfigShape,
  EnumOptions,
} from 'pipelines/StepShapeUtils';
import UserDefinedFunctionEditor from 'pipelines/user-defined-function/UserDefinedFunctionEditor';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {capitalize, constantCaseToWords} from 'util/NameUtil';
import {AllKeys, assertJSONObject, isJSONArray, JSONObject, JSONValue} from 'util/Types';

import ColumnDragAndDrop from '../editors/ColumnDragAndDrop';
import {
  ConnectionSelector,
  ConnectionFileSelector,
  ConnectionTransformDataTypeSelector,
} from '../editors/custom-editors/ConnectionDataEditor';
import {DataSourceTypeEditor, DataSourceEditor} from '../editors/custom-editors/CreateProcedureDatasetEditor';
import {DashboardBlockEditor} from '../editors/custom-editors/DashboardBlockEditor';
import DefineTemplateVariablesEditor from '../editors/custom-editors/DefineTemplateVariablesEditor';
import JoinEditor from '../editors/custom-editors/JoinEditor';
import LookUpConfigSectionEditor from '../editors/custom-editors/LookupColumnAndBy';
import LookUpFieldEditor from '../editors/custom-editors/LookUpFieldEditor';
import PlateSchemaQueryEditor from '../editors/custom-editors/PlateSchemaQueryEditor';
import RegistrySchemaQueryEditor from '../editors/custom-editors/RegistrySchemaQueryEditor';
import SeedItemsEditor from '../editors/custom-editors/SeedItemsEditor';
import UnionEditor from '../editors/custom-editors/UnionEditor';
import SearchEditor from '../editors/SearchEditor';
import {
  BANNER_KEY,
  ConfigDataFrameInfo,
  ConfigDataFramesInfos,
  ConfigShapePath,
  CUSTOM_COMPONENT_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  FieldUISchemaBanner,
  HAS_BACKEND_FIELD_GENERATOR,
  HIDDEN_FIELDS_KEY,
  isDiscreteColorPalette,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchemaV2,
  TAB_ORDER_UI_SCHEMA_KEY,
  NUM_COLUMNS_KEY,
  StepConfigEditorOverride,
  TaggedUnionFieldSettingsV2,
} from '../StepConfigEditorTypes';
import {BAR_CHART_CONFIG_SECTIONS, BAR_CHART_STYLE_SECTIONS} from './chart-schemas/BarChartSchema';
import {BOX_PLOT_CONFIG_SECTIONS, BOX_PLOT_STYLE_SECTIONS} from './chart-schemas/BoxPlotSchema';
import {createChartTypeSchema} from './chart-schemas/ChartSchemaHelpers';
import {HEATMAP_CONFIG_SECTIONS, HEATMAP_STYLE_SECTIONS} from './chart-schemas/HeatmapSchema';
import {LINE_CHART_CONFIG_SECTIONS, LINE_CHART_STYLE_SECTIONS} from './chart-schemas/LineChartSchema';
import {PIE_CHART_CONFIG_SECTIONS, PIE_CHART_STYLE_SECTIONS} from './chart-schemas/PieChartSchema';
import {
  SCATTER_CHART_CONFIG_SECTIONS,
  SCATTER_CHART_STYLE_SECTIONS,
} from './chart-schemas/ScatterChartSchema';

type OnChangeConfigMiddlewareFn<
  JsonSpec extends PipelineConfigJsonSpec,
  StepType extends PipelineStepType = PipelineStepType,
  StepVersion extends number = number,
> = (
  newConfig: PipelineJsonSpecToTsType<JsonSpec, 'draft'>,
  oldConfig: PipelineJsonSpecToTsType<JsonSpec, 'draft'>,
  dataFramesInfos: ConfigDataFramesInfos<StepType, StepVersion>
) => PipelineJsonSpecToTsType<JsonSpec, 'draft'>;

interface SharedComponentUISchema {
  displayName?: string;
  infoText?: string;
}

interface SharedComponentUISchemaV2 extends SharedComponentUISchema {
  configShapePath?: Array<string>;
}

interface CustomComponentUISchema<JsonSpec extends PipelineConfigJsonSpec> extends SharedComponentUISchema {
  [CUSTOM_COMPONENT_KEY]: (props: {
    config: PipelineJsonSpecToTsType<JsonSpec, 'draft'>;
    onChangeConfig: (newConfig: PipelineJsonSpecToTsType<JsonSpec, 'draft'>) => void;
    disabled?: boolean;
  }) => JSX.Element;
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: OnChangeConfigMiddlewareFn<JsonSpec>;
}

interface StandardComponentUISchema<JsonSpec extends PipelineConfigJsonSpec> extends SharedComponentUISchema {
  fieldSettings?: _GetJsonSpecUISchemaShape<JsonSpec>;
}

interface StandardComponentUISchemaV2<
  JsonSpec extends PipelineConfigJsonSpec,
> extends SharedComponentUISchemaV2 {
  fieldSettings?: _GetJsonSpecUISchemaShapeV2<JsonSpec>;
}

// See docstring for GetConfigShapeUISchema
type _GetJsonSpecUISchemaShape<JsonSpec extends PipelineConfigJsonSpec> = JsonSpec extends {
  kind: 'shape';
  fields: infer Fields extends Record<string, PipelineConfigJsonSpec>;
}
  ? {
      /**
       * Ordered array of variable names.
       *
       * Variable names that are not found in this array will be rendered in their original order after any
       * variables that _are_ in this array.
       */
      [FIELD_ORDER_UI_SCHEMA_KEY]?: ReadonlyArray<keyof Fields>;
      /**
       * Variables that should be hidden, meaning that they will not be configurable from the default step
       * config view.
       */
      [HIDDEN_FIELDS_KEY]?: ReadonlyArray<keyof Fields>;
      /**
       * A function that is called whenever the config changes.
       *
       * One example usage is to set the default of a field based on the values of other fields.
       *
       * It takes in the new config, the old config, and the data frame spec, and should return whatever
       * config should be set instead of the new config.
       */
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: OnChangeConfigMiddlewareFn<JsonSpec>;
      // Allows adding a banner to the top of the step.
      [BANNER_KEY]?: FieldUISchemaBanner;
      // Indicates that step has a backend API to update step configuration.
      [HAS_BACKEND_FIELD_GENERATOR]?: boolean;
    } & {
      // Must be assignable to `ShapeUISchema`
      [FieldName in keyof Fields]?:
        | StandardComponentUISchema<Fields[FieldName]>
        | CustomComponentUISchema<Fields[FieldName]>;
    }
  : JsonSpec extends {kind: 'array'; elementSpec: infer ElementSpec extends PipelineConfigJsonSpec}
    ? _GetJsonSpecUISchemaShape<ElementSpec>
    : JsonSpec extends {
          kind: 'taggedUnion';
          tagKey: infer TagKey extends string;
          choices: ReadonlyArray<infer Choice extends GetPipelineConfigSpecType<'shape'>>;
        }
      ? {
          // Must be assignable to `TaggedUnionUISchema`
          placeholder?: string;
          infoText?: string;
          tagKeyDisplayNames?: {[TagChoice in GetTaggedUnionTagValues<JsonSpec>]?: string};
          choiceSettings?: _GetJsonSpecUISchemaShape<Choice>;
          /**
           * Ordered array of variable names. It should not include the tag key, since that is always rendered
           * first.
           *
           * Variable names that are not found in this array will be rendered in their original order after any
           * variables that _are_ in this array.
           */
          [FIELD_ORDER_UI_SCHEMA_KEY]?: ReadonlyArray<Exclude<AllKeys<Choice['fields']>, TagKey>>;
          /**
           * Variables that should be hidden, meaning that they will not be configurable from the default step
           * config view.
           */
          [HIDDEN_FIELDS_KEY]?: ReadonlyArray<Exclude<AllKeys<Choice['fields']>, TagKey>>;
          /**
           * A function that is called whenever the config changes.
           *
           * One example usage is to set the default of a field based on the values of other fields.
           *
           * It takes in the new config, the old config, and the data frame spec, and should return whatever
           * config should be set instead of the new config.
           */
          [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: OnChangeConfigMiddlewareFn<JsonSpec>;
        }
      : JsonSpec extends {kind: 'enum'; choices: ReadonlyArray<infer Choice extends string | number>}
        ? // Must be assignable to `EnumUISchema`
          {displayNames?: {[key in Choice]?: string}; defaultValue?: Choice; placeholder?: string}
        : JsonSpec extends {kind: 'enum'; choices: ReadonlyArray<EnumOptions>}
          ? // Must be assignable to `EnumUISchema`
            {defaultValue?: string | number; placeholder?: string}
          : never;
type _GetConfigShapeUISchema<ConfigShape extends StepConfigShape> = _GetJsonSpecUISchemaShape<{
  kind: 'shape';
  required: true;
  fields: ConfigShape;
}>;

/**
 * Get the UI schema for a step config shape.
 *
 * This is a recursive function that traverses the shape of the step config and returns a UI schema based
 * on the types of the encountered shapes.
 *
 * All UI schema fields are optional.
 *
 * Also supports specifying a custom component for a field, instead of inferring the schema for the field
 * from the spec.
 *
 * Behavior:
 *   * `kind: 'shape'` -> `{displayName: string; fieldSettings: Record<FieldName, (Recursively applied to field type)>}`
 *   * `kind: 'array'` -> `(Recursively applied to array's elementSpec)`
 *   * `kind: 'taggedUnion'` -> `{tagKeyDisplayNames: Record<TagKey, string>; choiceSettings: (Recursively applied to choice types)>}`
 *     Note that all choices will be combined into the same UI Schema, rather than giving each choice its
 *     own schema. This is for simplicity since usually the fields in each choice are relatively similar.
 *   * `kind: 'enum'` -> `{displayNames: Record<EnumChoice, string>}`
 *   * Anything else -> `never` (no UI schema supported); this is the leaves of the tree
 *
 * For example, if your step config shape is:
 * ```
 * {a: {kind: 'string'}, b: {kind: 'enum', choices: ['foo', 'bar']}}
 * ```
 * then the UI schema will be:
 *
 * ```ts
 * {
 *   a: {displayName: 'A display name'},
 *   b: {
 *     displayName: 'B display name',
 *     fieldSettings: {displayNames: {foo: 'Foo', bar: 'Bar'}} // all optional
 *   }
 * }
 * ```
 *
 * See more examples below in STEP_UI_SCHEMAS.
 */
export type StepTypeConfigUISchema<
  StepType extends PipelineStepType,
  StepVersion extends number,
> = GetPipelineStepShape<StepType, StepVersion>['config'] extends StepConfigShape
  ? _GetConfigShapeUISchema<GetPipelineStepShape<StepType, StepVersion>['config']>
  : never;

type _GetJsonSpecUISchemaShapeV2<JsonSpec extends PipelineConfigJsonSpec> = JsonSpec extends {
  kind: 'shape';
  fields: infer Fields extends Record<string, PipelineConfigJsonSpec>;
}
  ? {
      // Only variable names present in this array will be rendered.
      [FIELD_ORDER_UI_SCHEMA_KEY]: ReadonlyArray<keyof Fields>;
      [SECTION_NAME_UI_SCHEMA_KEY]?: string;
    } & {
      [K in keyof Fields]: StandardComponentUISchemaV2<Fields[K]>;
    }
  : JsonSpec extends {kind: 'taggedUnion'}
    ? TaggedUnionFieldSettingsV2
    : never;

export interface TabContainerUISchema {
  displayName: string;
  uiSchema: ShapeUISchemaV2;
}

export interface TabbedUISchema {
  [TAB_ORDER_UI_SCHEMA_KEY]: ReadonlyArray<string>;
  [HAS_BACKEND_FIELD_GENERATOR]?: boolean;
  [key: string]: TabContainerUISchema;
}

export type StepTypeConfigUISchemaV2<
  StepType extends PipelineStepType,
  StepVersion extends number,
> = GetPipelineStepShape<StepType, StepVersion>['config'] extends StepConfigShape
  ? ShapeUISchemaV2 | TabbedUISchema
  : never;

const AGGREGATION_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.AGGREGATE_DATASET][0].config.aggregations.elementSpec;

const WINDOW_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.WINDOW_FUNCTIONS][0].config.windows.elementSpec;

const FIND_AND_REPLACE_OPTIONS_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.FIND_AND_REPLACE][0].config.findAndReplaceOptions;

const AGGREGATE_TAG_KEY_DISPLAY_NAME_EXCEPTIONS: Record<string, string> = {
  STRING_AGG: 'Text concat',
};

/** @package */
export const AGGREGATE_TAG_KEY_DISPLAY_NAMES: Record<
  GetTaggedUnionTagValues<typeof AGGREGATION_ELEMENT_SHAPE>,
  string
> = objectFromEntries(
  AGGREGATION_ELEMENT_SHAPE.choices
    .map((choice) => choice.fields[AGGREGATION_ELEMENT_SHAPE.tagKey].value)
    .map((tagValue) => [
      tagValue,
      AGGREGATE_TAG_KEY_DISPLAY_NAME_EXCEPTIONS[tagValue] ?? capitalize(constantCaseToWords(tagValue)),
    ])
);

/** @package */
export const WINDOW_TAG_KEY_DISPLAY_NAMES: Record<
  GetTaggedUnionTagValues<typeof WINDOW_ELEMENT_SHAPE>,
  string
> = objectFromEntries(
  WINDOW_ELEMENT_SHAPE.choices
    .map((choice) => choice.fields[WINDOW_ELEMENT_SHAPE.tagKey].value)
    .map((tagValue) => [tagValue, capitalize(constantCaseToWords(tagValue))])
);

const AGGREGATE_OR_WINDOW_TAG_KEY_DISPLAY_NAMES = {
  ...AGGREGATE_TAG_KEY_DISPLAY_NAMES,
  ...WINDOW_TAG_KEY_DISPLAY_NAMES,
};

const FUNCTIONS_WITHOUT_COLUMN = new Set([
  'COUNT',
  'CUME_DIST',
  'DENSE_RANK',
  'PERCENT_RANK',
  'RANK',
  'ROW_NUMBER',
]);

/** @package */
export function onChangeAggregationOrWindowMiddleware<
  T extends
    | PipelineJsonSpecToTsType<typeof AGGREGATION_ELEMENT_SHAPE, 'draft'>
    | PipelineJsonSpecToTsType<typeof WINDOW_ELEMENT_SHAPE, 'draft'>,
>(
  newConfig: T,
  oldConfig: T,
  dataFramesInfos: ConfigDataFramesInfos<
    PipelineStepType.AGGREGATE_DATASET | PipelineStepType.WINDOW_FUNCTIONS,
    0
  >
): T {
  if (newConfig?.aggregationFunction == null) {
    return newConfig;
  }
  if (FUNCTIONS_WITHOUT_COLUMN.has(newConfig.aggregationFunction)) {
    return {
      ...newConfig,
      newColumnName:
        newConfig.newColumnName ?? AGGREGATE_OR_WINDOW_TAG_KEY_DISPLAY_NAMES[newConfig.aggregationFunction],
    };
  } else if (
    // If aggregation function and column are set, _and_ one of those is being changed, then
    // set the new column name to <Aggregation fn>(<Column name>)
    // eslint-disable-next-line benchlint/no-in-operator
    'column' in newConfig &&
    newConfig.column != null &&
    (oldConfig?.aggregationFunction !== newConfig.aggregationFunction ||
      oldConfig?.column !== newConfig.column)
  ) {
    const newColumnName = `${AGGREGATE_OR_WINDOW_TAG_KEY_DISPLAY_NAMES[newConfig.aggregationFunction]} of ${
      findAndAssertColumn(dataFramesInfos['Input dataset'][0].dataFrameSpec, newConfig.column).displayName
    }`;
    // If the aggregation function is STRING_AGG, also set the default separator to an empty string
    return newConfig.aggregationFunction === 'STRING_AGG'
      ? {...newConfig, newColumnName, separator: newConfig.separator ?? ''}
      : {...newConfig, newColumnName};
  }
  return newConfig;
}

/** @package */
export const FIND_AND_REPLACE_TAG_KEY_DISPLAY_NAMES: Record<
  GetTaggedUnionTagValues<typeof FIND_AND_REPLACE_OPTIONS_SHAPE>,
  string
> = objectFromEntries([
  ['LITERAL', capitalize('Text')],
  ['REGEX', `${capitalize('Regular expressions')} (RE2 syntax)`], // Leave RE2 out so its not uncapitalized
]);

/** @package */
export function onChangeFindAndReplaceMiddleware<
  T extends PipelineJsonSpecToTsType<typeof FIND_AND_REPLACE_OPTIONS_SHAPE, 'draft'>,
>(newConfig: T, oldConfig: T): T {
  if (newConfig?.findAndReplaceType == null) {
    return newConfig;
  }
  const sharedOptions = {
    matchAllOccurrences: false,
    replacementString: '',
  };
  if (newConfig?.findAndReplaceType === 'LITERAL') {
    return {
      literalString: '',
      matchWholeWord: true,
      ...sharedOptions,
      ...newConfig,
    };
  } else {
    return {
      patternString: '',
      ...sharedOptions,
      ...newConfig,
    };
  }
}

export const AGGREGATE_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.AGGREGATE_DATASET, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['aggregations', 'groupColumns'],
  groupColumns: {displayName: 'Group by'},
  aggregations: {
    displayName: 'Aggregate',
    fieldSettings: {
      tagKeyDisplayNames: AGGREGATE_TAG_KEY_DISPLAY_NAMES,
      placeholder: 'Select function',
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'newColumnName', 'separator', 'orderByColumns'],
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeAggregationOrWindowMiddleware,
      choiceSettings: {
        aggregationFunction: {displayName: 'Aggregation function'},
        column: {displayName: 'Column to aggregate'},
        newColumnName: {displayName: 'New column name'},
        separator: {displayName: 'Separator'},
        orderByColumns: {
          displayName: 'Order by',
          fieldSettings: {
            column: {displayName: 'Column'},
            direction: {
              displayName: 'Direction',
              fieldSettings: {
                placeholder: 'Select direction',
                defaultValue: 'ASCENDING',
                displayNames: {
                  ASCENDING: 'Ascending',
                  DESCENDING: 'Descending',
                },
              },
            },
          },
        },
      },
    },
  },
};

export const APPLY_COLUMN_MAPPING_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.APPLY_COLUMN_MAPPING,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['outputColumns', 'keepUnmappedColumns'],
  outputColumns: {
    displayName: 'Output columns',
    fieldSettings: {
      tagKeyDisplayNames: {
        POSITION: 'Position',
        NAME: 'Name',
        REGEX: 'Regular expression',
      },
      placeholder: 'Select match type',
      [FIELD_ORDER_UI_SCHEMA_KEY]: [
        'columnPosition',
        'caseSensitive',
        'regEx',
        'displayName',
        'createEmptyColumnIfNotFound',
      ],
      choiceSettings: {
        matchBy: {displayName: 'Match by'},
        columnPosition: {displayName: 'Column position'},
        caseSensitive: {displayName: 'Case sensitive'},
        regEx: {displayName: 'Regex pattern'},
        displayName: {displayName: 'Output column name'},
        createEmptyColumnIfNotFound: {displayName: 'Create empty column if no match'},
      },
    },
  },
  keepUnmappedColumns: {
    displayName: 'Keep unmapped columns',
    infoText: 'Include columns from the input that were not mapped to an output column',
  },
};

export const BIN_DATA_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.BIN_DATA, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'numBins'],
  column: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['column'],
    column: {displayName: 'Column', configShapePath: ['column']},
  },
  numBins: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['numBins'],
    numBins: {displayName: 'Number of bins', configShapePath: ['numBins']},
  },
};

export const BOLTZGEN_SUCCESS_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.BOLTZGEN_SUCCESS, 0> = {};

export const CONVERT_ASM_TO_CSV_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CONVERT_ASM_TO_CSV,
  0
> = {
  [HAS_BACKEND_FIELD_GENERATOR]: true,
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['transformType'],
  transformType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['transformType'],
    transformType: {
      displayName: 'Transform Type',
      configShapePath: ['transformType'],
      fieldSettings: {placeholder: 'Select transform type'},
    },
  },
};

export const CONVERT_FILE_TO_DATASET_WITH_DATA_ENTRY_ASSISTANT_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CONVERT_FILE_TO_DATASET_WITH_DATA_ENTRY_ASSISTANT,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['datasetName', 'instructions', 'columns'],
  [BANNER_KEY]: {
    text: 'This feature is powered by AI, and may make mistakes. Always verify the accuracy of AI-generated content.',
    supportingInfo:
      'To generate the requested content, the AI system will analyze the content of the input file. For more information on AI security and privacy, [look here](https://help.benchling.com/hc/en-us/articles/20901323667853-Security-and-privacy-for-LLMs-and-AI-powered-features).',
    bannerType: InlineBannerType.ATTENTION,
    placement: 'below',
  },
  datasetName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['datasetName'],
    datasetName: {
      displayName: 'Dataset Name',
      configShapePath: ['datasetName'],
    },
  },
  instructions: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['instructions'],
    instructions: {
      displayName: 'Add instructions',
      infoText:
        'Include any additional instructions or clarifications to help the AI system understand how to create the dataset',
      configShapePath: ['instructions'],
    },
  },
  columns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['columns'],
    columns: {
      displayName: 'Columns',
      infoText:
        'The column names and types to use for generation. If empty, the step will use AI to generate suggested columns.',
      configShapePath: ['columns'],
      [BANNER_KEY]: {
        text: 'Columns will be automatically generated by AI if not specified',
        supportingInfo: 'Use the + above to add a list of specific columns',
        bannerType: InlineBannerType.ATTENTION,
        placement: 'below',
        initialShowMore: true,
      },
      elementUISchema: {
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'data_type'],
        name: {
          displayName: 'Name',
          configShapePath: ['name'],
        },
        data_type: {
          displayName: 'Data Type',
          configShapePath: ['data_type'],
        },
      },
    },
  },
};

export const CONVERT_JSON_TO_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CONVERT_JSON_TO_DATASET,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'columns'],
  name: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['name'],
    name: {
      displayName: 'Dataset Name',
      configShapePath: ['name'],
    },
  },
  columns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['columns'],
    columns: {
      displayName: 'Columns',
      configShapePath: ['columns'],
      elementUISchema: {
        [NUM_COLUMNS_KEY]: 2,
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'path', 'include', 'required'],
        name: {
          displayName: 'Column Name',
          infoText: "May include another column value in name via '$' substitution e.g.: 'ColName $unit$'.",
          configShapePath: ['name'],
        },
        path: {
          displayName: 'JSON Path to Column Value',
          infoText: "Full path to the JSON leaf value to use for the column, e.g.: 'path/to/leaf'.",
          configShapePath: ['path'],
        },
        include: {
          displayName: 'Include in dataset?',
          infoText: "May set to 'No' if using as part of another column name.",
          configShapePath: ['include'],
        },
        required: {
          displayName: 'Required?',
          infoText: 'Dataset creation will fail if the JSON path is missing from the input.',
          configShapePath: ['required'],
        },
      },
    },
  },
};

export const CREATE_METERING_USAGE_CHART_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CREATE_METERING_USAGE_CHART,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['dateRange', 'productType', 'userId'],
  [NUM_COLUMNS_KEY]: 3,
  dateRange: {
    displayName: 'Date range',
    configShapePath: ['dateRange'],
    editorOverride: StepConfigEditorOverride.DATE_RANGE,
    fieldSettings: {
      startKey: 'startDate',
      endKey: 'endDate',
    },
  },
  productType: {
    displayName: 'Product type',
    configShapePath: ['productType'],
    fieldSettings: {
      displayNames: {
        AI_ASK: 'AI Ask',
        COMPOSE: 'Compose',
        DATA_ENTRY_AGENT: 'Data Entry Agent',
        DEEP_RESEARCH: 'Deep Research',
        STRUCTURE_PREDICTION: 'Structure Prediction',
      },
    },
  },
  userId: {
    displayName: 'User ID',
    configShapePath: ['userId'],
  },
};

export const CREATE_PROCEDURE_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CREATE_PROCEDURE_DATASET,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataSourceType', 'dataSource', 'seedItems'],
  dataSourceType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataSourceType'],
    dataSourceType: {
      [CUSTOM_COMPONENT_KEY]: DataSourceTypeEditor,
    },
  },
  dataSource: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataSource'],
    dataSource: {
      [CUSTOM_COMPONENT_KEY]: DataSourceEditor,
    },
  },
  seedItems: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['seedItems'],
    seedItems: {
      [CUSTOM_COMPONENT_KEY]: SeedItemsEditor,
    },
  },
};

export const EXTRACT_DATA_FRAME_VALUE_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.EXTRACT_DATA_FRAME_VALUE,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['columnSystemName', 'rowIndex'],
  columnSystemName: {
    displayName: 'Column',
    configShapePath: ['columnSystemName'],
  },
  rowIndex: {
    displayName: 'Row Index',
    infoText: 'Index of the row to extract value from',
    configShapePath: ['rowIndex'],
  },
};

export const MAP_JSON_TO_DATASETS_AND_FILES_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.MAP_JSON_TO_DATASETS_AND_FILES,
  0
> = {
  datasets: {
    displayName: 'Datasets',
    fieldSettings: {
      name: {
        displayName: 'Dataset Name',
      },
      columns: {
        displayName: 'Columns',
        fieldSettings: {
          name: {
            displayName: 'Column Name',
            infoText: "May include another column value in name via '$' substitution e.g.: 'ColName $unit$'.",
          },
          path: {
            displayName: 'JSON Path to Column Value',
            infoText: "Full path to the JSON leaf value to use for the column, e.g.: 'path/to/leaf'.",
          },
          include: {
            displayName: 'Include in dataset?',
            infoText: "May set to 'No' if using as part of another column name.",
          },
          required: {
            displayName: 'Required?',
            infoText: 'Dataset creation will fail if the JSON path is missing from the input.',
          },
        },
      },
      include: {
        displayName: 'Include in output?',
        infoText: "May set to 'No' if adding to another dataset via a join transform.",
      },
      is_metadata: {
        displayName: 'Output as metadata JSON file?',
        infoText:
          'If set, dataset will be output as a file containing a JSON blob. Must have a single value per column.',
      },
    },
  },
  transforms: {
    displayName: 'Transforms',
    fieldSettings: {
      placeholder: 'Select a transform',
      choiceSettings: {
        type: {
          displayName: 'Transform Type',
        },
        dataset_1: {
          displayName: 'Dataset 1',
        },
        dataset_2: {
          displayName: 'Dataset 2',
        },
        join_key_1: {
          displayName: 'Dataset 1 Join Column',
        },
        join_key_2: {
          displayName: 'Dataset 2 Join Column',
        },
        dataset: {
          displayName: 'Dataset',
        },
        path: {
          displayName: 'JSON Path to list to pivot on',
        },
        value_path: {
          displayName: 'JSON Path to leaf value to use for pivot column value',
        },
        label_path: {
          displayName: 'JSON Path to leaf value to use for pivot column name',
        },
      },
    },
  },
};

export const CONVERT_TO_ASM_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.CONVERT_TO_ASM, 0> = {
  [HAS_BACKEND_FIELD_GENERATOR]: true,
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['asmVendor', 'transformType'],
  asmVendor: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['asmVendor'],
    asmVendor: {
      displayName: 'Instrument Vendor',
      configShapePath: ['asmVendor'],
      fieldSettings: {placeholder: 'Select vendor'},
    },
  },
  transformType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['transformType'],
    transformType: {
      displayName: 'CSV Transform Type',
      infoText: 'If specified, output file will be transformed CSV.',
      configShapePath: ['transformType'],
      fieldSettings: {placeholder: 'Optionally select CSV transform type'},
    },
  },
};

export const CREATE_DATASET_FROM_FILE_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CREATE_DATASET_FROM_FILE,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'datasetName',
    'csvDelimiter',
    'customCsvDelimiter',
    'dataLocale',
    'sheetName',
  ],
  datasetName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['datasetName'],
    datasetName: {
      displayName: 'Dataset name',
      configShapePath: ['datasetName'],
    },
  },
  csvDelimiter: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['csvDelimiter'],
    csvDelimiter: {
      displayName: 'CSV delimiter',
      configShapePath: ['csvDelimiter'],
      fieldSettings: {
        defaultValue: ',',
      },
    },
  },
  customCsvDelimiter: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['customCsvDelimiter'],
    customCsvDelimiter: {
      displayName: 'Custom CSV delimiter',
      infoText: "Only used if 'Custom' is selected for CSV delimiter.",
      configShapePath: ['customCsvDelimiter'],
    },
  },
  dataLocale: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataLocale'],
    dataLocale: {
      displayName: 'Data locale',
      configShapePath: ['dataLocale'],
      fieldSettings: {
        defaultValue: 'en-US',
      },
    },
  },
  sheetName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['sheetName'],
    sheetName: {
      displayName: 'Spreadsheet sheet name',
      infoText: 'Used to specify the sheet/tab to use for multi-sheet Excel files.',
      configShapePath: ['sheetName'],
    },
  },
};

export const EXTRACT_OR_REMOVE_LINES_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.EXTRACT_OR_REMOVE_LINES,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['actionType', 'startLine', 'endLine'],
  actionType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['actionType'],
    actionType: {
      displayName: 'Action type',
      configShapePath: ['actionType'],
      fieldSettings: {
        defaultValue: 'EXTRACT_LINES',
      },
    },
  },
  startLine: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['startLine'],
    startLine: {displayName: 'Start line', configShapePath: ['startLine']},
  },
  endLine: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['endLine'],
    endLine: {displayName: 'End line', configShapePath: ['endLine']},
  },
};

const FORMULA_ELEMENT_SHAPE = PIPELINE_STEP_SHAPES[PipelineStepType.COMPUTED_COLUMN][0].config.formula;
interface ComputedColumnConfigJsonSpec {
  kind: 'shape';
  required: true;
  fields: GetPipelineStepShape<PipelineStepType.COMPUTED_COLUMN, 0>['config'];
}
export const onChangeFormulaElementMiddleware: OnChangeConfigMiddlewareFn<
  typeof FORMULA_ELEMENT_SHAPE,
  PipelineStepType.COMPUTED_COLUMN,
  0
> = (newConfig, oldConfig, dataFramesInfos) => {
  if (newConfig.formulaText == null) {
    return {...newConfig, formulaAst: null};
  }
  try {
    return {...newConfig, formulaAst: formulaTextToFormulaAstJson(newConfig.formulaText)};
  } catch (e) {
    if (e instanceof AnalysisFormulaParsingError) {
      return {...newConfig, formulaAst: null};
    } else {
      throw e;
    }
  }
};

export const onChangeComputedColumnConfigMiddleware: OnChangeConfigMiddlewareFn<
  ComputedColumnConfigJsonSpec,
  PipelineStepType.COMPUTED_COLUMN,
  0
> = (newConfig, oldConfig, dataFramesInfos) => {
  if (newConfig?.replaceColumn == null || newConfig.replaceColumn === oldConfig?.replaceColumn) {
    return newConfig;
  }
  const selectedColumn = dataFramesInfos['Input dataset']?.[0]?.dataFrameSpec.columns.find(
    (column) => column.systemName === newConfig.replaceColumn
  );
  if (selectedColumn?.displayName == null) {
    return newConfig;
  }
  return {...newConfig, newColumnName: selectedColumn.displayName};
};

export const COMPUTED_COLUMN_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.COMPUTED_COLUMN, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['newColumnName', 'replaceColumn', 'formula'],
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeComputedColumnConfigMiddleware,
  newColumnName: {displayName: 'New column name'},
  replaceColumn: {
    displayName: 'Replace column (optional)',
    infoText:
      'If specified, this column will be replaced in-place. If left blank, a new column will be added.',
  },
  formula: {
    displayName: 'Formula text',
    [CUSTOM_COMPONENT_KEY]: AnalysisFormulaEditor,
    [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeFormulaElementMiddleware,
  },
};

const PIVOT_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.PIVOT_DATASET][0].config.valueColumns.elementSpec;

/** @package */
export const onChangePivotElementMiddleware: OnChangeConfigMiddlewareFn<
  typeof PIVOT_ELEMENT_SHAPE,
  PipelineStepType.PIVOT_DATASET,
  0
> = (newConfig, oldConfig, dataFramesInfos) => {
  if (newConfig?.aggregationFunction == null) {
    return newConfig;
  }
  if (
    // If newColumnName is not set, and a column is set, and one of aggregation functions or columns is being
    // changed, then set the new column name to <Aggregation fn>(<Column name>)
    newConfig.newColumnName == null &&
    newConfig.column != null &&
    (oldConfig?.aggregationFunction !== newConfig.aggregationFunction ||
      oldConfig?.column !== newConfig.column)
  ) {
    return {
      ...newConfig,
      newColumnName: `${AGGREGATE_TAG_KEY_DISPLAY_NAMES[newConfig.aggregationFunction]} of ${
        findAndAssertColumn(dataFramesInfos['Input dataset'][0].dataFrameSpec, newConfig.column).displayName
      }`,
    };
  }
  return newConfig;
};

export const PIVOT_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.PIVOT_DATASET, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['pivotColumns', 'valueColumns', 'groupColumns'],
  pivotColumns: {displayName: 'Pivot columns'},
  valueColumns: {
    displayName: 'Values',
    fieldSettings: {
      tagKeyDisplayNames: AGGREGATE_TAG_KEY_DISPLAY_NAMES,
      placeholder: 'Select function',
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'newColumnName'],
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangePivotElementMiddleware,
      choiceSettings: {
        aggregationFunction: {displayName: 'Aggregation function'},
        column: {displayName: 'Column to aggregate'},
        newColumnName: {displayName: 'New column name'},
      },
    },
  },
  groupColumns: {displayName: 'Pivot rows'},
};

export const RUN_HIGHRES_PROTOCOL_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.RUN_HIGHRES_PROTOCOL,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'scientificProtocolId',
    'name',
    'requestedByUserId',
    'cellarioSystemId',
    'ports',
  ],
  [HAS_BACKEND_FIELD_GENERATOR]: true,
  scientificProtocolId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['scientificProtocolId'],
    scientificProtocolId: {
      displayName: 'Scientific Protocol',
      infoText:
        'The HighRes scientific protocol to execute. This determines the available ports and Cellario systems.',
      configShapePath: ['scientificProtocolId'],
    },
  },
  name: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['name'],
    name: {
      displayName: 'Name',
      infoText: 'A name for the work order that will be created in HighRes.',
      configShapePath: ['name'],
    },
  },
  requestedByUserId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['requestedByUserId'],
    requestedByUserId: {
      displayName: 'Requested by',
      infoText: 'The HighRes user who is requesting this work order.',
      configShapePath: ['requestedByUserId'],
    },
  },
  cellarioSystemId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['cellarioSystemId'],
    cellarioSystemId: {
      displayName: 'Cellario System',
      infoText:
        'The Cellario system that will execute the protocol. Available systems depend on the selected scientific protocol.',
      configShapePath: ['cellarioSystemId'],
    },
  },
  ports: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['ports'],
    ports: {
      displayName: 'Ports',
      infoText:
        'Configure the input/output ports for the protocol. Each port maps plate barcodes to a labware type.',
      configShapePath: ['ports'],
      elementUISchema: {
        [NUM_COLUMNS_KEY]: 2,
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['portId', 'labwareTypeId', 'plateIdColumn'],
        portId: {
          displayName: 'Port',
          infoText: 'The protocol port. Available ports depend on the selected scientific protocol.',
          configShapePath: ['portId'],
        },
        labwareTypeId: {
          displayName: 'Labware Type',
          infoText: 'The type of labware (plate format) used for this port.',
          configShapePath: ['labwareTypeId'],
        },
        plateIdColumn: {
          displayName: 'Plate Column',
          infoText: 'The column in the input dataset that contains the plate barcodes for this port.',
          configShapePath: ['plateIdColumn'],
        },
      },
    },
  },
};

export const SEND_USER_NOTIFICATION_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.SEND_USER_NOTIFICATION,
  0
> = {
  // In V2, fields not in FIELD_ORDER are automatically hidden.
  // notificationType is a required enum but an internal implementation detail not shown to users.
  // A defaultValue is needed so makeInitialConfigState produces a valid config.
  [FIELD_ORDER_UI_SCHEMA_KEY]: [],
  notificationType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['notificationType'],
    notificationType: {
      displayName: 'Notification type',
      configShapePath: ['notificationType'],
      fieldSettings: {
        defaultValue: 'NDC_INGESTED',
      },
    },
  },
};

export const SORT_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.SORT_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['sortOptions'],
  sortOptions: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['sortOptions'],
    sortOptions: {
      displayName: 'Sort by',
      configShapePath: ['sortOptions'],
      elementUISchema: {
        [NUM_COLUMNS_KEY]: 2,
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'direction'],
        column: {
          displayName: 'Column',
          configShapePath: ['column'],
        },
        direction: {
          displayName: 'Direction',
          configShapePath: ['direction'],
          fieldSettings: {
            defaultValue: 'ASCENDING',
            displayNames: {
              ASCENDING: 'Ascending',
              DESCENDING: 'Descending',
            },
          },
        },
      },
    },
  },
};

export const UNPIVOT_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.UNPIVOT_DATASET, 0> = {
  // In V2, excludedColumns is automatically hidden by omitting from FIELD_ORDER.
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['unpivotColumns', 'nameColumn', 'valueColumn'],
  unpivotColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['unpivotColumns'],
    unpivotColumns: {displayName: 'Unpivot columns', configShapePath: ['unpivotColumns']},
  },
  nameColumn: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['nameColumn'],
    nameColumn: {displayName: 'Name column', configShapePath: ['nameColumn']},
  },
  valueColumn: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['valueColumn'],
    valueColumn: {displayName: 'Value column', configShapePath: ['valueColumn']},
  },
};

export const WAREHOUSE_SQL_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.WAREHOUSE_SQL_DATASET,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName', 'parameterizedSQLQuery'],
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Table name',
      configShapePath: ['dataFrameName'],
    },
  },
  parameterizedSQLQuery: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['parameterizedSQLQuery'],
    parameterizedSQLQuery: {
      displayName: 'SQL query',
      infoText: 'Write a SQL query against the data warehouse to create a table.',
      [CUSTOM_COMPONENT_KEY]: (props) => (
        <UserDefinedFunctionEditor {...props} fieldName="parameterizedSQLQuery" mode="sql" />
      ),
    },
  },
};

export const WINDOW_FUNCTIONS_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.WINDOW_FUNCTIONS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['windows', 'orderByColumns'],
  orderByColumns: {
    displayName: 'Order by',
    fieldSettings: {
      column: {displayName: 'Column'},
      direction: {
        displayName: 'Direction',
        fieldSettings: {
          placeholder: 'Select direction',
          defaultValue: 'ASCENDING',
          displayNames: {
            ASCENDING: 'Ascending',
            DESCENDING: 'Descending',
          },
        },
      },
    },
  },
  windows: {
    displayName: 'Windows',
    fieldSettings: {
      tagKeyDisplayNames: WINDOW_TAG_KEY_DISPLAY_NAMES,
      placeholder: 'Select function',
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'newColumnName'],
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeAggregationOrWindowMiddleware,
      choiceSettings: {
        aggregationFunction: {displayName: 'Window function'},
        column: {displayName: 'Column to aggregate'},
        newColumnName: {displayName: 'New column name'},
        partitionColumns: {displayName: 'Partition by'},
        orderByColumns: {
          displayName: 'Order by',
          fieldSettings: {
            column: {displayName: 'Column'},
            direction: {
              displayName: 'Direction',
              fieldSettings: {
                placeholder: 'Select direction',
                defaultValue: 'ASCENDING',
                displayNames: {
                  ASCENDING: 'Ascending',
                  DESCENDING: 'Descending',
                },
              },
            },
          },
        },
      },
    },
  },
};

export const FIND_AND_REPLACE_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.FIND_AND_REPLACE, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['selectedColumns', 'findAndReplaceOptions'],
  selectedColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['selectedColumns'],
    selectedColumns: {
      displayName: 'Columns',
      infoText:
        'Specify which text columns to find and replace values in. If no columns are selected, all text columns are included.',
      configShapePath: ['selectedColumns'],
    },
  },
  findAndReplaceOptions: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['findAndReplaceOptions'],
    findAndReplaceOptions: {
      displayName: 'Options',
      configShapePath: ['findAndReplaceOptions'],
      fieldSettings: {
        tagKeyDisplayNames: FIND_AND_REPLACE_TAG_KEY_DISPLAY_NAMES,
        tagKeyDisplayName: 'Search with text or regular expressions?',
        choiceFieldOrder: [
          'literalString',
          'patternString',
          'replacementString',
          'matchWholeWord',
          'matchAllOccurrences',
        ],
        [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: (newValue, _path, _oldConfig) =>
          onChangeFindAndReplaceMiddleware(
            newValue as PipelineJsonSpecToTsType<typeof FIND_AND_REPLACE_OPTIONS_SHAPE, 'draft'>,
            _oldConfig as PipelineJsonSpecToTsType<typeof FIND_AND_REPLACE_OPTIONS_SHAPE, 'draft'>
          ),
        choiceUISchema: {
          literalString: {displayName: 'Search text', configShapePath: ['literalString']},
          patternString: {displayName: 'Regex pattern', configShapePath: ['patternString']},
          matchWholeWord: {displayName: 'Match whole word?', configShapePath: ['matchWholeWord']},
          matchAllOccurrences: {
            displayName: 'Match all occurrences within a cell?',
            configShapePath: ['matchAllOccurrences'],
          },
          replacementString: {displayName: 'Replacement string', configShapePath: ['replacementString']},
        },
      },
    },
  },
};

export const HIDE_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.HIDE_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['columns', 'columnsToKeep'],
  columns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['columns'],
    columns: {
      displayName: 'Columns to hide',
      configShapePath: ['columns'],
    },
  },
  columnsToKeep: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['columnsToKeep'],
    columnsToKeep: {
      displayName: 'Columns to keep',
      configShapePath: ['columnsToKeep'],
    },
  },
};

export const RETYPE_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.RETYPE_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['retypeColumns'],
  retypeColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['retypeColumns'],
    retypeColumns: {
      displayName: 'Column formats',
      configShapePath: ['retypeColumns'],
      elementUISchema: {
        [NUM_COLUMNS_KEY]: 2,
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'type'],
        column: {
          displayName: 'Column',
          configShapePath: ['column'],
        },
        type: {
          displayName: 'Data format',
          configShapePath: ['type'],
          fieldSettings: {
            defaultValue: 'INT',
            displayNames: TYPES_TO_DISPLAY_NAMES,
          },
        },
      },
    },
  },
};

export const RENAME_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.RENAME_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['renameColumns'],
  renameColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['renameColumns'],
    renameColumns: {
      displayName: 'Columns',
      configShapePath: ['renameColumns'],
      elementUISchema: {
        [NUM_COLUMNS_KEY]: 2,
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'name'],
        column: {
          displayName: 'Column',
          configShapePath: ['column'],
        },
        name: {
          displayName: 'New name',
          configShapePath: ['name'],
        },
      },
    },
  },
};

export const REORDER_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.REORDER_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['reorderColumns'],
  reorderColumns: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['reorderColumns'],
    reorderColumns: {
      displayName: 'Columns',
      [CUSTOM_COMPONENT_KEY]: ColumnDragAndDrop,
    },
  },
};

const IS_INPUT_X_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.REGRESSION_INTERPOLATION][0].config.isInputX;
export const onChangeIsInputXMiddleware: OnChangeConfigMiddlewareFn<
  typeof IS_INPUT_X_ELEMENT_SHAPE,
  PipelineStepType.REGRESSION_INTERPOLATION,
  0
> = (newConfig, oldConfig, inputDataFrames) => newConfig;

export const REGRESSION_INTERPOLATION_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.REGRESSION_INTERPOLATION,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['inputColumn', 'isInputX', 'inputPartitionColumn'],
  [HIDDEN_FIELDS_KEY]: ['selectedPartitionValue'],
  inputColumn: {displayName: 'Input column'},
  isInputX: {
    displayName: 'Select prediction mode',
    infoText:
      'Whether the input column values will be treated as values of X or values of Y when calculating the interpolated values.',
    [CUSTOM_COMPONENT_KEY]: IsInputXToggleGroup,
    [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeIsInputXMiddleware,
  },
  inputPartitionColumn: {
    displayName: 'Input series column (optional)',
    infoText:
      "If specified, then the parameter table (regression model) and the input table will be joined based on this column's values.",
  },
};

/**
 * Middleware function that automatically updates chart series colors when the discrete color palette changes.
 *
 * This function serves as a configuration change handler that ensures visual consistency
 * when users switch between different color palettes (e.g., BENCHLING, DARK24, etc.).
 * When the discreteColorPalette field is modified, this middleware regenerates colors
 * for all existing series in the chart configuration, cycling through the new palette's
 * available colors to maintain unique color assignments.
 *
 * @param newValue - The new discrete color palette name (e.g., 'BENCHLING', 'DARK24')
 * @param path - The configuration path being modified (must be ['chartConfig', 'parameters', 'discreteColorPalette'])
 * @param oldConfig - The current pipeline step configuration containing chart settings
 * @param dataFramesInfos - DataFrame information (unused but required by middleware interface)
 * @returns Updated configuration with regenerated series colors, or original config if no changes needed
 *
 * @example
 * // When user changes palette from 'BENCHLING' to 'DARK24':
 * // - All existing series colors are reassigned using DARK24 palette colors
 * // - Colors cycle through palette if more series than available colors
 * // - Original series order and other properties are preserved
 *
 * @package
 */
export function onChangeDiscreteColorPaletteMiddleware(
  newValue: string,
  path: ConfigShapePath,
  oldConfig: Record<string, JSONValue>,
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
): Record<string, JSONValue> {
  const isChangingPalette =
    JSON.stringify(path) === JSON.stringify(['chartConfig', 'parameters', 'discreteColorPalette']);

  if (isChangingPalette) {
    const paletteName: DiscreteColorPalette = isDiscreteColorPalette(newValue) ? newValue : 'BENCHLING';
    const palette = COLOR_PALETTES[paletteName];

    const chartConfig = oldConfig?.chartConfig;
    assertJSONObject(chartConfig);
    const parameters = chartConfig.parameters;
    assertJSONObject(parameters);
    const seriesColorConfigs = parameters.seriesColorConfigs;

    if (!isJSONArray(seriesColorConfigs) || seriesColorConfigs.length === 0) {
      return oldConfig;
    }

    const newSeriesColorConfigs = seriesColorConfigs.map(
      (mapping: JSONObject, idx: number): JSONObject => ({
        ...mapping,
        color: palette[idx % palette.length],
      })
    );

    return {
      ...oldConfig,
      chartConfig: {
        ...chartConfig,
        parameters: {
          ...parameters,
          discreteColorPalette: newValue,
          seriesColorConfigs: newSeriesColorConfigs,
        },
      },
    };
  }

  return oldConfig;
}

/**
 * Generates a default color configuration for a chart series based on the current palette and index.
 *
 * This function is used as a FIELD_DEFAULTS_GENERATOR_KEY to automatically assign colors
 * to chart series when new series are added or when the color palette changes. It cycles
 * through the available colors in the selected palette, ensuring each series gets a unique
 * color when possible.
 *
 * @param config - The pipeline step configuration containing chart settings
 * @param dataFramesInfos - DataFrame information (unused in this implementation but required by the generator interface)
 * @param idx - The index of the series (0-based), used to cycle through palette colors
 * @returns A JSONObject containing the color configuration with a 'color' property set to a hex color value
 *
 * @example
 * // For a series at index 0 with BENCHLING palette
 * getDefaultSeriesColorConfig(config, dataFramesInfos, 0)
 * // Returns: { color: "#000DB5" } // First color in the BENCHLING palette
 *
 */
export function getDefaultSeriesColorConfig(
  config: JSONObject,
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>,
  idx = 0
): JSONObject {
  const chartConfig = config.chartConfig;
  assertJSONObject(chartConfig);

  const parameters = chartConfig.parameters;
  assertJSONObject(parameters);

  // Check if the incoming palette is valid, if not, default to BENCHLING
  const discreteColorPalette: DiscreteColorPalette = isDiscreteColorPalette(parameters?.discreteColorPalette)
    ? parameters.discreteColorPalette
    : 'BENCHLING';
  const paletteHexColors = COLOR_PALETTES[discreteColorPalette];
  return {
    color: paletteHexColors[idx % paletteHexColors.length],
  };
}

export const CHART_VIEW_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.CHART_VIEW, 0> = {
  [TAB_ORDER_UI_SCHEMA_KEY]: ['configure', 'style'],

  configure: {
    displayName: 'Configure',
    uiSchema: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: [
        'boxPlotConfigure',
        'pieChartConfigure',
        'lineChartConfigure',
        'heatMapConfigure',
        'scatterChartConfigure',
        'barChartConfigure',
      ],
      boxPlotConfigure: createChartTypeSchema('BOX_PLOT', BOX_PLOT_CONFIG_SECTIONS),
      pieChartConfigure: createChartTypeSchema('PIE_CHART', PIE_CHART_CONFIG_SECTIONS),
      lineChartConfigure: createChartTypeSchema('LINE_CHART', LINE_CHART_CONFIG_SECTIONS),
      heatMapConfigure: createChartTypeSchema('HEATMAP', HEATMAP_CONFIG_SECTIONS),
      scatterChartConfigure: createChartTypeSchema('SCATTER_PLOT', SCATTER_CHART_CONFIG_SECTIONS),
      barChartConfigure: createChartTypeSchema('BAR_CHART', BAR_CHART_CONFIG_SECTIONS),
    },
  },
  style: {
    displayName: 'Style',
    uiSchema: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: [
        'boxPlotStyle',
        'pieChartStyle',
        'lineChartStyle',
        'heatmapStyle',
        'scatterChartStyle',
        'barChartStyle',
      ],
      boxPlotStyle: createChartTypeSchema('BOX_PLOT', BOX_PLOT_STYLE_SECTIONS),
      pieChartStyle: createChartTypeSchema('PIE_CHART', PIE_CHART_STYLE_SECTIONS),
      lineChartStyle: createChartTypeSchema('LINE_CHART', LINE_CHART_STYLE_SECTIONS),
      heatmapStyle: createChartTypeSchema('HEATMAP', HEATMAP_STYLE_SECTIONS),
      scatterChartStyle: createChartTypeSchema('SCATTER_PLOT', SCATTER_CHART_STYLE_SECTIONS),
      barChartStyle: createChartTypeSchema('BAR_CHART', BAR_CHART_STYLE_SECTIONS),
    },
  },
};

export const FILTER_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.FILTER_DATASET, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['filter'],
  filter: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['filter'],
    filter: {
      displayName: 'Column filters',
      [CUSTOM_COMPONENT_KEY]: FilterStepFilterConfig,
    },
  },
};

export const SEND_TO_CONNECTION_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.SEND_TO_CONNECTION,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId'],
  connectionId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId'],
    connectionId: {
      displayName: 'Connection',
      [CUSTOM_COMPONENT_KEY]: ({config: fullConfig, onChangeConfig, disabled}) => (
        <SearchEditor
          config={fullConfig.connectionId as string | null}
          onChangeConfig={(newValue) => {
            onChangeConfig(fullConfig, ['connectionId'], newValue);
          }}
          modelTypes={[ModelTypeConstants.CONNECTION]}
          disabled={disabled}
        />
      ),
    },
  },
};

export const USER_DEFINED_FUNCTION_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.USER_DEFINED_FUNCTION,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['userDefinedCode'],
  userDefinedCode: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['userDefinedCode'],
    userDefinedCode: {
      displayName: 'Code',
      [CUSTOM_COMPONENT_KEY]: (props) => <UserDefinedFunctionEditor {...props} fieldName="userDefinedCode" />,
    },
  },
};

export const DATA_CATALOG_FILE_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.DATA_CATALOG_FILE, 0> =
  {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['fileId', 'fileName'],
    fileId: {
      displayName: 'File',
      configShapePath: ['fileId'],
    },
    fileName: {
      displayName: 'File name',
      configShapePath: ['fileName'],
    },
  };

export const DATA_CATALOG_FILES_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.DATA_CATALOG_FILES,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['fileIds', 'fileBaseName'],
  fileIds: {
    displayName: 'Files',
    configShapePath: ['fileIds'],
  },
  fileBaseName: {
    displayName: 'Base file name',
    infoText:
      'When multiple files are selected, this will be prepended to the original file name. If only one file is selected, this will be the name of the file.',
    configShapePath: ['fileBaseName'],
  },
};

export const DATA_CATALOG_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.DATA_CATALOG_DATASET,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['datasetId', 'dataFrameName'],
  datasetId: {displayName: 'Dataset', configShapePath: ['datasetId']},
  dataFrameName: {displayName: 'Table name', configShapePath: ['dataFrameName']},
};

export const CONNECTION_DATA_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.CONNECTION_DATA_DATASET,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId', 'connectionFileId', 'transformDataType', 'dataFrameName'],
  connectionId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId'],
    connectionId: {
      displayName: 'Connection',
      [CUSTOM_COMPONENT_KEY]: ConnectionSelector,
    },
  },
  connectionFileId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionFileId'],
    connectionFileId: {
      displayName: 'Connection file',
      [CUSTOM_COMPONENT_KEY]: ConnectionFileSelector,
    },
  },
  transformDataType: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['transformDataType'],
    transformDataType: {
      displayName: 'Data type',
      [CUSTOM_COMPONENT_KEY]: ConnectionTransformDataTypeSelector,
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Table name',
      configShapePath: ['dataFrameName'],
    },
  },
};

const UPLOAD_CSV_MAX_FILE_SIZE_BYTES = 150 * 1024 * 1024;

export const UPLOAD_CSV_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.UPLOAD_CSV_DATASET,
  0
> = {
  // fileName is omitted from FIELD_ORDER because FileUploadEditor sets it automatically when a file is uploaded.
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['s3URL', 'dataFrameName'],
  s3URL: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['s3URL'],
    s3URL: {
      displayName: 'Upload CSV file',
      configShapePath: ['s3URL'],
      editorOverride: StepConfigEditorOverride.FILE_UPLOAD,
      fieldSettings: {
        fileNamePath: ['fileName'],
        accept: '.csv',
        maxFileSizeBytes: UPLOAD_CSV_MAX_FILE_SIZE_BYTES,
      },
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Table name',
      configShapePath: ['dataFrameName'],
    },
  },
};

export const INITIALIZE_DATASET_V2_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.INITIALIZE_DATASET,
  2
> = {
  // In V2, fields not in FIELD_ORDER are automatically hidden.
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['dashboardId', 'dataFrameName'],
  dashboardId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dashboardId'],
    dashboardId: {
      displayName: 'Dashboard and query',
      [CUSTOM_COMPONENT_KEY]: DashboardBlockEditor,
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Table name',
      configShapePath: ['dataFrameName'],
    },
  },
};

export const LOOK_UP_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.LOOK_UP, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['lookupConfig', 'schemaId', 'dataFrameName'],
  lookupConfig: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['lookupConfig'],
    lookupConfig: {
      [CUSTOM_COMPONENT_KEY]: LookUpConfigSectionEditor,
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Name table',
      configShapePath: ['dataFrameName'],
    },
  },
  schemaId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['schemaId'],
    schemaId: {
      displayName: BenchlingConfig.DOMAIN_GRAPH_ENABLE_EXPERIMENTAL_SCHEMAS
        ? 'Select entity, plate or container schema'
        : 'Select entity schema',
      infoText:
        "Choose the specific schema that matches the type of items in your lookup column. If the schema doesn't match your data, the step may fail or return no results.",
      [CUSTOM_COMPONENT_KEY]: LookUpFieldEditor,
    },
  },
};

export const JOIN_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.JOIN, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['join', 'dataFrameName'],
  join: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['join'],
    join: {[CUSTOM_COMPONENT_KEY]: JoinEditor},
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Name table',
      configShapePath: ['dataFrameName'],
    },
  },
};

export const UNION_DATASETS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.UNION_DATASETS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['union', 'includeDuplicates', 'dataFrameName'],
  union: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['union'],
    union: {[CUSTOM_COMPONENT_KEY]: UnionEditor},
  },
  includeDuplicates: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['includeDuplicates'],
    includeDuplicates: {
      displayName: 'Include duplicate rows',
      configShapePath: ['includeDuplicates'],
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Name table',
      configShapePath: ['dataFrameName'],
    },
  },
};

export const DEFINE_TEMPLATE_VARIABLES_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['variables'],
  variables: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['variables'],
    variables: {
      displayName: 'Define variables',
      [CUSTOM_COMPONENT_KEY]: DefineTemplateVariablesEditor,
    },
  },
};

export const REGISTRY_SCHEMA_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.REGISTRY_SCHEMA_DATASET,
  0
> = {
  // queryDescription and dataFrameName are omitted from FIELD_ORDER (hidden in V2).
  // dataFrameName is managed internally by RegistrySchemaQueryEditor.
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['query'],
  query: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['query'],
    query: {
      displayName: 'Registry schema query',
      [CUSTOM_COMPONENT_KEY]: RegistrySchemaQueryEditor,
    },
  },
};

export const PLATE_SCHEMA_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.PLATE_SCHEMA_DATASET,
  0
> = {
  // queryDescription, plateIds, and dataFrameName are omitted from FIELD_ORDER (hidden in V2).
  // plateIds and dataFrameName are managed internally by PlateSchemaQueryEditor.
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['query'],
  query: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['query'],
    query: {
      displayName: 'Plate schema query',
      [CUSTOM_COMPONENT_KEY]: PlateSchemaQueryEditor,
    },
  },
};
